import React from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  keyframes,
  styled
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Visibility } from '@mui/icons-material';
import { getProfile } from '../api/home';
import CustomThreeStar from '../components/Threejs/CustomThreeStar';
import CustomButton from '../components/common/CustomButton';
import resumePDF from '../assets/pdf/ginil.pdf';
import "./animation.css";

// Keyframe animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const GlassBox = styled(Box)(({ theme }) => ({
  backdropFilter: 'blur(12px)',
  background: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  padding: theme.spacing(4),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
}));

const AnimatedSection = styled(Box)({
  animation: `${fadeIn} 0.8s ease-out forwards`,
  opacity: 0,
});

const HomeScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    data: profileData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 1000 * 60 * 5,
  });

  const getAnimationDelay = (index) => ({
    animationDelay: `${index * 0.2}s`
  });

  const handleDownloadResume = () => {
    window.open(resumePDF, '_blank', 'noopener,noreferrer');
  };

  if (isError) {
    return (
      <Box sx={styles.errorContainer}>
        <Typography variant="h6" sx={{ color: 'error.main', mb: 2 }}>
          Error loading profile: {error.message}
        </Typography>
        <CustomButton
          label="Retry"
          onClick={() => window.location.reload()}
          sx={{ backgroundColor: 'primary.main' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.resumeContainer}>
      <CustomThreeStar />

      <Grid container spacing={3} sx={styles.gridContainer}>
        <Grid item xs={12} md={4} sx={styles.imageContainer}>
          <Avatar
            variant="square"
            src={profileData?.data?.profileImg}
            alt="Profile"
            sx={styles.avatar}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <GlassBox>
            {isLoading ? (
              <Box sx={styles.loadingContainer}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              <>
                <AnimatedSection style={getAnimationDelay(0)}>
                  <Typography variant="h4" component="h1" sx={styles.nameText}>
                    {profileData?.data?.fullname}
                  </Typography>
                </AnimatedSection>

                <AnimatedSection style={getAnimationDelay(1)}>
                  <Typography variant="h5" component="h2" sx={styles.designationText}>
                    {profileData?.data?.designation}
                  </Typography>
                </AnimatedSection>

                <AnimatedSection style={getAnimationDelay(2)}>
                  <Typography variant="h6" sx={styles.sectionHeader}>About Me</Typography>
                  <Typography variant="body1" sx={styles.bioText}>
                    {profileData?.data?.bio}
                  </Typography>
                </AnimatedSection>

                <AnimatedSection style={getAnimationDelay(3)}>
                  <Typography variant="h6" sx={styles.sectionHeader}>Experience</Typography>
                  <Typography variant="body1" sx={styles.experienceText}>
                    {profileData?.data?.experience}
                  </Typography>
                </AnimatedSection>
              </>
            )}

            {!isLoading && (
              <Box sx={styles.buttonContainer}>
                <CustomButton
                  onClick={handleDownloadResume}
                  sx={styles.downloadButton}
                  label="Download Resume"
                  fullWidth={isMobile}
                />
              </Box>
            )}
          </GlassBox>
        </Grid>
      </Grid>

      <Box sx={styles.viewCounter}>
        <Visibility fontSize="small" />
        <Typography variant="body2" sx={{ ml: 1 }}>
          {profileData?.data?.visitCount}
        </Typography>
      </Box>
    </Box>
  );
};

const styles = {
  resumeContainer: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    px: { xl: 10, lg: 8, md: 6, xs: 3 },
    py: 4,
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #1f1c2c, #928dab)',
  },

  gridContainer: {
    pt: '9vh',
    position: 'relative',
    zIndex: 1,
  },

  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    mt: { xl: 10, lg: 10, md: 1, sm: 1, xs: 1 },
  },

  avatar: {
    height: 250,
    width: 250,
    borderRadius: '20px',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '2px solid rgba(255,255,255,0.3)',
  },

  nameText: {
    mb: 2,
    color: 'white',
    fontWeight: 'bold',
    letterSpacing: 1,
    fontFamily: 'Outfit-ExtraBold',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
  },

  designationText: {
    mb: 4,
    color: '#00e5ff',
    fontFamily: 'Outfit-ExtraBold',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
  },

  sectionHeader: {
    mb: 2,
    color: '#ff4081',
    letterSpacing: 1,
    fontFamily: 'Outfit-ExtraBold',
    mt: 3,
  },

  bioText: {
    mb: 3,
    color: 'white',
    textAlign: { xs: 'center', md: 'justify' },
    letterSpacing: 0.5,
    fontFamily: 'Outfit-Regular',
    lineHeight: 1.8,
  },

  experienceText: {
    color: '#ccc',
    fontSize: '1.1rem',
    pb: 5,
    fontFamily: 'Outfit-Regular',
    lineHeight: 1.8,
  },

  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    py: 2,
  },

  downloadButton: {
    width: { xl: '15%', lg: '20%', md: '25%', sm: '40%', xs: '100%' },
  },

  viewCounter: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '20px',
    zIndex: 999,
    animation: 'fadeIn 1s ease-out 1.5s forwards',
    opacity: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    backdropFilter: 'blur(10px)',
  },

  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
  },

  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    gap: 2,
    backgroundColor: 'background.paper',
  },
};

export default HomeScreen;
